import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Loader2, CheckCircle2, UserPlus, Briefcase, Wrench, MapPin, Landmark, Building2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { NovatusLogo } from "@/components/NovatusLogo";
import { Button } from "@/components/ui/button";

// --- Reusable form helpers (module-level, NOT inside the component, so
// React doesn't re-mount the inputs on every keystroke and lose focus). ---

const Section = ({ icon: Icon, title, children }) => (
  <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-3">
    <div className="flex items-center gap-2 mb-3 pb-3 border-b border-zinc-800">
      <Icon className="text-[#00E575]" size={18} />
      <h2 className="font-display text-lg font-semibold">{title}</h2>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
  </div>
);

const Field = ({ label, value, onChange, name, type = "text", req = false, full = false, placeholder = "", inputMode, autoCapitalize, testId }) => (
  <div className={full ? "sm:col-span-2" : ""}>
    <label className="block text-[12px] font-semibold text-zinc-300 mb-1.5 uppercase tracking-widest">
      {label} {req && <span className="text-[#00E575]">*</span>}
    </label>
    <input
      data-testid={testId || `register-${name}-input`}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      inputMode={inputMode}
      autoCapitalize={autoCapitalize}
      className="h-12 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 text-zinc-50 text-base focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
    />
  </div>
);

// Profile-completion page reached AFTER the phone has been OTP-verified via
// /auth/start. The phone arrives as a URL param so we can survive refreshes.
//
// On submit → POST /auth/register → status: pending → admin approval.
export default function RegisterProfile() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const phone = params.get("phone") || "";
  const prefillEmail = params.get("email") || "";
  const promoFromUrl = params.get("promo") || sessionStorage.getItem("novatus_promo_code") || "";

  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: prefillEmail,
    phone: "",
    role: "sales_rep",
    street: "",
    house_number: "",
    zip: "",
    city: "",
    iban: "",
    bic: "",
    account_holder: "",
    company_name: "",
    vat_id: "",
    commercial_register: "",
    tax_number: "",
    installer_company_new: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [emailConflict, setEmailConflict] = useState(null);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  // Debounced live-check of the typed email so we can warn before submit
  useEffect(() => {
    const e = form.email.trim();
    const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    if (!isValid) {
      setEmailConflict(null);
      return;
    }
    const handle = setTimeout(() => {
      api.get(`/auth/check-email?email=${encodeURIComponent(e)}`)
        .then((r) => {
          if (r.data && r.data.conflict) {
            setEmailConflict({ email: e, status: r.data.status });
          } else {
            setEmailConflict(null);
          }
        })
        .catch(() => setEmailConflict(null));
    }, 500);
    return () => clearTimeout(handle);
  }, [form.email]);

  // Identity must have been pre-verified (phone via SMS-OTP, or email via
  // Email-OTP). If neither identifier is set, bounce back to /login.
  useEffect(() => {
    if (!phone && !prefillEmail) {
      navigate("/login", { replace: true });
      return;
    }
    let cancelled = false;
    const q = phone
      ? `phone=${encodeURIComponent(phone)}`
      : `email=${encodeURIComponent(prefillEmail)}`;
    api.get(`/auth/registration-status?${q}`)
      .then((r) => {
        if (cancelled) return;
        if (!r.data?.verified) {
          toast.error("Bestätigung abgelaufen. Bitte erneut anmelden.", {
            description: "Aus Sicherheitsgründen läuft die Bestätigung nach 30 Minuten ab.",
            duration: 5000,
          });
          navigate("/login", { replace: true });
        }
      })
      .catch(() => {
        if (!cancelled) navigate("/login", { replace: true });
      });
    return () => { cancelled = true; };
  }, [phone, prefillEmail, navigate]);

  // PLZ → City + Streets auto-complete via backend proxy (cached)
  const [streetSuggestions, setStreetSuggestions] = useState([]);
  useEffect(() => {
    const zip = (form.zip || "").trim();
    if (!/^\d{5}$/.test(zip)) {
      setStreetSuggestions([]);
      return;
    }
    let cancelled = false;
    api.get(`/plz/localities?postalCode=${zip}`)
      .then((r) => {
        if (cancelled || !r.data || r.data.length === 0) return;
        // Only autofill city if it's empty or differs from suggestion
        if (!form.city.trim() || form.city.trim() !== r.data[0].name) {
          setForm((f) => ({ ...f, city: r.data[0].name }));
        }
      })
      .catch(() => {});
    api.get(`/plz/streets?postalCode=${zip}`)
      .then((r) => { if (!cancelled) setStreetSuggestions(r.data || []); })
      .catch(() => { if (!cancelled) setStreetSuggestions([]); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.zip]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  // IBAN-Validierung (Format + Modulo-97 Prüfsumme — gleiche Logik wie
  // Backend, damit der User sofort Feedback bekommt vor Submit).
  const isValidIban = (input) => {
    const clean = (input || "").replace(/\s/g, "").toUpperCase();
    if (clean.length < 15 || clean.length > 34) return false;
    if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(clean)) return false;
    // Rearrange: move first 4 chars to end, then convert letters A-Z to 10-35
    const rearranged = (clean.slice(4) + clean.slice(0, 4))
      .split("")
      .map((c) => (c >= "A" && c <= "Z" ? (c.charCodeAt(0) - 55).toString() : c))
      .join("");
    // Modulo-97 on a big integer string (process 9 digits at a time)
    let remainder = 0;
    for (let i = 0; i < rearranged.length; i += 9) {
      const chunk = remainder.toString() + rearranged.slice(i, i + 9);
      remainder = parseInt(chunk, 10) % 97;
    }
    return remainder === 1;
  };

  // When user came in via Email-OTP, phone is now a required form field
  // (still needed for billing). When they came via SMS-OTP, the phone is
  // already known and stored in `phone` URL param.
  const phoneRequired = !phone && !!prefillEmail;
  const required = ["first_name", "last_name", "email", "street", "house_number", "zip", "city", "iban"];
  if (phoneRequired) required.push("phone");
  const missingRequired = required.filter((k) => !form[k]?.trim());
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const ibanValid = isValidIban(form.iban);
  const canSubmit = missingRequired.length === 0 && emailValid && ibanValid && !loading && !emailConflict && acceptedLegal;

  const submit = async () => {
    if (!emailValid) return toast.error("Bitte gültige E-Mail-Adresse eingeben");
    if (!ibanValid) return toast.error("IBAN sieht ungültig aus");
    if (missingRequired.length > 0) {
      return toast.error(`Bitte alle Pflichtfelder ausfüllen (fehlend: ${missingRequired.join(", ")})`);
    }
    if (!acceptedLegal) {
      return toast.error("Bitte AGB und Datenschutzerklärung akzeptieren");
    }
    setLoading(true);
    try {
      // Build clean payload — never include installer_company_new for sales_rep
      const payload = {
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        email: form.email.trim(),
        role: form.role,
        street: form.street.trim(),
        house_number: form.house_number.trim(),
        zip: form.zip.trim(),
        city: form.city.trim(),
        iban: form.iban.replace(/\s/g, "").toUpperCase(),
        bic: form.bic.toUpperCase(),
        account_holder: form.account_holder.trim(),
        company_name: form.company_name.trim(),
        vat_id: form.vat_id.toUpperCase(),
        commercial_register: form.commercial_register.trim(),
        tax_number: form.tax_number.trim(),
        dob: form.dob,
        phone: phone || (form.phone || "").trim(),
        legal_acceptance_slugs: ["agb", "datenschutz"],
        promo_code: promoFromUrl || undefined,
      };
      if (form.role === "installer" && form.installer_company_new) {
        payload.installer_company_new = { name: form.installer_company_new.trim() };
      }
      const res = await api.post("/auth/register", payload);
      // Clear once consumed
      if (promoFromUrl) sessionStorage.removeItem("novatus_promo_code");
      // Auto-login with the issued token; the next route depends on whether
      // plausibility passed (→ /checkout) or the account needs admin review
      // (→ dedicated waiting page).
      if (res.data?.token && res.data?.user) {
        login(res.data.token, res.data.user);
        const next = res.data.next_step;
        if (next === "pending_admin") {
          toast.success("Konto angelegt — wir prüfen Ihre Daten", {
            description: "Sie erhalten eine E-Mail mit dem Bezahllink, sobald freigegeben.",
            duration: 6000,
          });
          navigate("/register/pending-admin", { replace: true });
          return;
        }
        toast.success("Konto angelegt — weiter zur Bezahlung");
        // Persist promo so Checkout auto-applies it
        if (promoFromUrl) sessionStorage.setItem("novatus_promo_code", promoFromUrl);
        navigate("/select-plan", { replace: true });
        return;
      }
      // Fallback: legacy flow without token
      setDone(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "already_registered") {
        // Show a friendly card with a Login CTA
        setEmailConflict({
          email: detail.email || form.email.trim(),
          field: detail.field,
          status: detail.status,
          message: detail.message,
        });
        toast.error(detail.message || "Konto existiert bereits");
        // Scroll up so user sees the warning card
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else if (detail && typeof detail === "object"
                 && (detail.code === "phone_not_verified" || detail.code === "not_verified")) {
        toast.error("Bestätigung abgelaufen — bitte erneut anmelden.", {
          description: "Sie werden zur Login-Seite weitergeleitet.",
          duration: 4000,
        });
        setTimeout(() => navigate("/login", { replace: true }), 2000);
      } else {
        toast.error(typeof detail === "string" ? detail : detail?.message || "Registrierung fehlgeschlagen");
      }
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center px-6">
        <div className="w-full max-w-md text-center">
          <NovatusLogo size="md" />
          <div className="mt-8 bg-[#18181B] border border-[#00E575]/30 rounded-2xl p-8">
            <div className="w-16 h-16 bg-[#00E575]/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="text-[#00E575]" size={32} />
            </div>
            <h1 className="font-display text-2xl font-semibold mb-2">Vielen Dank, {form.first_name}!</h1>
            <p className="text-sm text-zinc-400 mb-2 leading-relaxed">
              Ihre Registrierung wurde erfolgreich übermittelt.
            </p>
            <p className="text-sm text-zinc-400 mb-6 leading-relaxed">
              Bitte prüfen Sie Ihr Postfach unter{" "}
              <span className="font-mono text-zinc-200">{form.email}</span> und
              <strong className="text-[#00E575]"> bestätigen Sie Ihre E-Mail-Adresse</strong> über den Link in der Mail.
              Nach Bestätigung wird Ihre Anfrage an den Administrator weitergeleitet.
            </p>
            <Button
              onClick={() => navigate("/login")}
              data-testid="register-success-back-btn"
              className="w-full h-12 bg-[#00E575] text-[#09090B] font-semibold hover:bg-[#00C964]"
            >
              Zurück zum Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] px-4 py-8">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate("/login")}
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-zinc-100 mb-5"
          data-testid="register-back-link"
        >
          <ArrowLeft size={18} /> Zurück
        </button>

        <div className="mb-6">
          <NovatusLogo size="md" />
        </div>

        <div className="mb-6">
          <h1 className="font-display text-3xl font-semibold">Profil vervollständigen</h1>
          <p className="text-sm text-zinc-400 mt-1">
            {phone ? (
              <>Ihre Mobilnummer <span className="font-mono text-[#00E575]">{phone}</span> ist verifiziert ✓ —{" "}
                bitte ergänzen Sie nun Ihre Profildaten.</>
            ) : (
              <>Ihre E-Mail-Adresse <span className="font-mono text-[#00E575]">{prefillEmail}</span> ist verifiziert ✓ —{" "}
                bitte ergänzen Sie nun Ihre Profildaten.</>
            )}
          </p>
        </div>

        {/* Conflict warning (live + after submit) */}
        {emailConflict && (
          <div
            data-testid="register-email-conflict-banner"
            className="mb-3 p-4 bg-amber-500/10 border-2 border-amber-500/60 rounded-xl"
          >
            <p className="text-sm font-semibold text-amber-200 mb-1">
              Diese E-Mail-Adresse ist bereits einem aktiven Konto zugeordnet
            </p>
            <p className="text-xs text-amber-100/80 mb-3 leading-relaxed">
              {emailConflict.message ||
                `Die E-Mail-Adresse ${emailConflict.email} wird bereits verwendet. Bitte verwenden Sie eine andere E-Mail, oder loggen Sie sich mit Ihrer Mobilnummer ein.`}
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => navigate("/login")}
                data-testid="register-goto-login-btn"
                className="h-10 bg-amber-500 text-[#09090B] font-semibold hover:bg-amber-400 text-xs px-4"
              >
                Zum Login →
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  setForm({ ...form, email: "" });
                  setEmailConflict(null);
                }}
                className="h-10 border-zinc-700 text-zinc-300 text-xs px-4"
              >
                Andere E-Mail eingeben
              </Button>
            </div>
          </div>
        )}

        {/* Rolle */}
        <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 mb-3">
          <h2 className="font-display text-lg font-semibold mb-3">Ich bin…</h2>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setForm({ ...form, role: "sales_rep" })}
              data-testid="register-role-rep"
              className={`h-16 rounded-xl border text-left px-4 transition flex items-center gap-3 ${form.role === "sales_rep" ? "border-[#00E575] bg-[#00E575]/10 text-zinc-50" : "border-zinc-700 bg-[#09090B] text-zinc-400 hover:border-zinc-500"}`}
            >
              <Briefcase size={20} className={form.role === "sales_rep" ? "text-[#00E575]" : ""} />
              <div>
                <div className="text-sm font-semibold">Vertrieb</div>
                <div className="text-[12px] text-zinc-300">Sales / Berater</div>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setForm({ ...form, role: "installer" })}
              data-testid="register-role-installer"
              className={`h-16 rounded-xl border text-left px-4 transition flex items-center gap-3 ${form.role === "installer" ? "border-violet-500 bg-violet-500/10 text-zinc-50" : "border-zinc-700 bg-[#09090B] text-zinc-400 hover:border-zinc-500"}`}
            >
              <Wrench size={20} className={form.role === "installer" ? "text-violet-400" : ""} />
              <div>
                <div className="text-sm font-semibold">Installation</div>
                <div className="text-[12px] text-zinc-300">Handwerksbetrieb</div>
              </div>
            </button>
          </div>
        </div>

        {/* Persönlich */}
        <Section icon={UserPlus} title="Persönliche Daten">
          <Field label="Vorname" name="first_name" value={form.first_name} onChange={set("first_name")} req placeholder="Max" />
          <Field label="Nachname" name="last_name" value={form.last_name} onChange={set("last_name")} req placeholder="Mustermann" />
          {prefillEmail ? (
            <div className="sm:col-span-2">
              <label className="block text-[12px] font-semibold text-zinc-300 mb-1.5 uppercase tracking-widest">
                E-Mail <span className="text-[#00E575]">*</span>
                <span className="ml-2 text-[12px] text-[#00E575] font-medium normal-case tracking-normal">(per OTP verifiziert)</span>
              </label>
              <input
                data-testid="register-email-input"
                type="email"
                value={form.email}
                readOnly
                className="h-12 w-full bg-[#0c1410] border border-[#00E575]/40 rounded-xl px-4 text-zinc-200 text-base font-mono cursor-not-allowed"
              />
            </div>
          ) : (
            <Field label="E-Mail" name="email" value={form.email} onChange={set("email")} type="email" req full placeholder="max@example.com" inputMode="email" autoCapitalize="none" />
          )}
          {phoneRequired && (
            <Field label="Mobilnummer" name="phone" value={form.phone} onChange={set("phone")} type="tel" req full placeholder="+491701234567" inputMode="tel" />
          )}
          <Field label="Geburtsdatum" name="dob" value={form.dob} onChange={set("dob")} type="date" />
        </Section>

        {/* Adresse */}
        <Section icon={MapPin} title="Adresse">
          <div className="sm:col-span-2">
            <label className="block text-[12px] font-semibold text-zinc-300 mb-1.5 uppercase tracking-widest">
              Straße <span className="text-[#00E575]">*</span>
            </label>
            <input
              data-testid="register-street-input"
              type="text"
              value={form.street}
              onChange={set("street")}
              placeholder="Hauptstraße"
              list="register-street-suggestions"
              className="h-12 w-full bg-[#09090B] border border-zinc-700 rounded-xl px-4 text-zinc-50 text-base focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] outline-none"
            />
            <datalist id="register-street-suggestions">
              {streetSuggestions.map((s, i) => (
                <option key={i} value={s.name} />
              ))}
            </datalist>
            {streetSuggestions.length > 0 && (
              <p className="text-[12px] text-zinc-300 mt-1">{streetSuggestions.length} Straßen für PLZ {form.zip} gefunden</p>
            )}
          </div>
          <Field label="Hausnummer" name="house_number" value={form.house_number} onChange={set("house_number")} req placeholder="42a" />
          <Field label="PLZ" name="zip" value={form.zip} onChange={set("zip")} req placeholder="10115" inputMode="numeric" />
          <Field label="Ort" name="city" value={form.city} onChange={set("city")} req full placeholder="Berlin" />
        </Section>

        {/* Bank */}
        <Section icon={Landmark} title="Bankverbindung">
          <div className="sm:col-span-2">
            <label className="block text-[12px] font-semibold text-zinc-300 mb-1.5 uppercase tracking-widest">
              IBAN <span className="text-[#00E575]">*</span>
            </label>
            <input
              data-testid="register-iban-input"
              type="text"
              value={form.iban}
              onChange={set("iban")}
              placeholder="DE12 5001 0517 0648 4898 90"
              autoCapitalize="characters"
              className={`h-12 w-full bg-[#09090B] border rounded-xl px-4 text-zinc-50 text-base focus:ring-1 outline-none ${
                form.iban.replace(/\s/g, "").length >= 15
                  ? (ibanValid
                      ? "border-[#00E575] focus:border-[#00E575] focus:ring-[#00E575]"
                      : "border-red-500 focus:border-red-500 focus:ring-red-500")
                  : "border-zinc-700 focus:border-[#00E575] focus:ring-[#00E575]"
              }`}
            />
            {form.iban.replace(/\s/g, "").length >= 15 && (
              <p className={`text-[12px] mt-1 ${ibanValid ? "text-[#00E575]" : "text-red-400"}`}>
                {ibanValid
                  ? "✓ IBAN gültig (für SEPA-Lastschrift verwendbar)"
                  : "✗ IBAN ungültig — bitte Prüfsumme prüfen"}
              </p>
            )}
            <p className="text-[12px] text-zinc-300 mt-1.5">
              Wir nutzen die IBAN für den monatlichen Beitragseinzug per SEPA-Lastschrift.
            </p>
          </div>
          <Field label="BIC" name="bic" value={form.bic} onChange={set("bic")} placeholder="INGDDEFFXXX" autoCapitalize="characters" />
          <Field label="Kontoinhaber" name="account_holder" value={form.account_holder} onChange={set("account_holder")} placeholder="(falls abweichend)" />
        </Section>

        {/* Business — installer only */}
        {form.role === "installer" && (
          <Section icon={Building2} title="Geschäftlich">
            <Field label="Firmenname" name="company_name" value={form.company_name} onChange={set("company_name")} full placeholder="Müller GmbH" />
            <Field label="USt-ID" name="vat_id" value={form.vat_id} onChange={set("vat_id")} placeholder="DE123456789" autoCapitalize="characters" />
            <Field label="Steuernummer" name="tax_number" value={form.tax_number} onChange={set("tax_number")} placeholder="12/345/67890" />
            <Field label="Handelsregister" name="commercial_register" value={form.commercial_register} onChange={set("commercial_register")} full placeholder="HRB 12345 Berlin" />
            <Field label="Installations-Firmenname (intern)" name="installer_company_new" value={form.installer_company_new} onChange={set("installer_company_new")} full placeholder="z. B. Heizungsbau Müller GmbH" />
          </Section>
        )}

        <div
          data-testid="legal-acceptance-section"
          className="mt-6 bg-[#18181B] border border-zinc-800 rounded-2xl p-4 sm:p-5"
        >
          <label className="flex items-start gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={acceptedLegal}
              onChange={(e) => setAcceptedLegal(e.target.checked)}
              data-testid="legal-accept-checkbox"
              className="mt-1 h-5 w-5 rounded border-zinc-600 bg-zinc-900 accent-[#00C964] cursor-pointer"
            />
            <span className="text-sm text-zinc-300 leading-relaxed">
              Ich habe die{" "}
              <a
                href="/legal/agb"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00E575] underline hover:text-[#00C964]"
                data-testid="legal-link-agb"
              >
                Allgemeinen Nutzungsbedingungen
              </a>{" "}
              und die{" "}
              <a
                href="/legal/datenschutz"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00E575] underline hover:text-[#00C964]"
                data-testid="legal-link-datenschutz"
              >
                Datenschutzerklärung
              </a>{" "}
              gelesen und akzeptiere sie. Das{" "}
              <a
                href="/legal/impressum"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 underline hover:text-zinc-200"
                data-testid="legal-link-impressum"
              >
                Impressum
              </a>{" "}
              habe ich zur Kenntnis genommen.
            </span>
          </label>
        </div>

        <Button
          data-testid="register-submit-btn"
          onClick={submit}
          disabled={!canSubmit}
          className="h-14 w-full bg-[#00E575] text-[#09090B] font-bold text-lg rounded-xl hover:bg-[#00C964] glow-green active:scale-[0.97] transition-all disabled:opacity-50 mt-2"
        >
          {loading ? <Loader2 className="animate-spin" /> : "Registrierung absenden"}
        </Button>
        {!canSubmit && !loading && missingRequired.length > 0 && (
          <p className="text-xs text-amber-400 text-center mt-2">
            Fehlende Pflichtfelder: {missingRequired.length}
          </p>
        )}
        {!canSubmit && !loading && missingRequired.length === 0 && !acceptedLegal && (
          <p className="text-xs text-amber-400 text-center mt-2">
            Bitte AGB und Datenschutzerklärung akzeptieren
          </p>
        )}
      </div>
    </div>
  );
}
